import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { GalleryAsset } from '@xphoto/shared';
import { GRID_GAP, type DensityConfig } from './config';
import { computeJustifiedLayout, type LayoutRow } from './justified-layout';
import { useElementWidth } from './use-element-width';
import { Thumbnail } from './Thumbnail';

/**
 * Grille justifiée **virtualisée** (XPHOTO-18).
 *
 * 1. On collecte les assets déjà chargés (préfixe dense depuis l'offset 0) et on
 *    calcule une disposition justifiée (rangées à hauteur cible, ratio préservé).
 * 2. `@tanstack/react-virtual` ne monte que les rangées visibles ; la hauteur
 *    totale du scroller vient d'abord du layout connu, puis d'une estimation du
 *    reste (assets pas encore chargés) pour que la barre de défilement reflète
 *    le `total` fourni par l'API.
 * 3. Le défilement déclenche `ensureRange` (chargement fenêtré des pages à venir).
 * 4. La grille arbitre aussi la SÉLECTION de la tuile (XPHOTO-32 lot 2) : elle porte
 *    l'ancre du maj-clic et résout la plage en ids, parce qu'elle seule sait faire
 *    index → asset. Le MODE sélection, lui, ne se dérive plus ici (v1.41.0) : il vient
 *    de `GalleryView`, seul à connaître à la fois la sélection et le bouton
 *    « Sélectionner » de la barre d'outils.
 *
 * CORRECTIF « lignes vides au-delà de ~100 » : la clé est de mapper la position de
 * défilement (y compris dans les rangées ESTIMÉES, au-delà du préfixe chargé) vers
 * un index d'asset absolu cible, puis de demander le chargement CONTIGU jusque-là.
 * Dans l'estimé, on projette `placedCount + (rangéesAu-delà) × tuiles/rangée`. Le
 * hook charge alors les pages contiguës manquantes (dédoublonnées, plafonnées par
 * tick), et chaque page étend le préfixe dense → la grille se remplit au défilement.
 */
interface GridProps {
  total: number;
  /** Frontière du préfixe dense chargé (déclenche la recomposition au fil des pages). */
  loadedCount: number;
  density: DensityConfig;
  getAsset: (index: number) => GalleryAsset | undefined;
  ensureRange: (start: number, end: number) => void;
  selected: ReadonlySet<string>;
  /**
   * Mode sélection actif — ÉTAT résolu par `GalleryView` (`mode explicite ||
   * selected.size > 0`) et non plus dérivé ici (v1.41.0). Reçu en BOOLÉEN : la
   * mémoïsation des tuiles en dépend (cf. `Thumbnail`), un objet la casserait.
   */
  selectionMode: boolean;
  onToggleSelect: (id: string) => void;
  /**
   * AJOUTE une plage d'ids à la sélection (maj-clic, XPHOTO-32 lot 2). La grille est la
   * seule à savoir résoudre un index → asset : elle remonte donc des IDS, jamais des
   * index bruts.
   */
  onSelectRange: (ids: string[]) => void;
  /** Ouvre la visionneuse à l'index absolu (T3, XPHOTO-21). */
  onOpenAsset: (index: number) => void;
}

export function Grid({
  total,
  loadedCount,
  density,
  getAsset,
  ensureRange,
  selected,
  selectionMode,
  onToggleSelect,
  onSelectRange,
  onOpenAsset,
}: GridProps): React.JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(scrollRef);

  /**
   * ANCRE du maj-clic : index de la dernière tuile cochée par un clic SIMPLE.
   *
   * - Pas d'ancre (aucun clic simple depuis le montage) → le maj-clic retombe sur une
   *   bascule simple, et POSE l'ancre : le second maj-clic sélectionne alors la plage.
   * - Un maj-clic ne DÉPLACE PAS l'ancre (patron des explorateurs de fichiers) : on peut
   *   ré-étendre ou réduire la même plage sans repartir de zéro.
   * - En `ref` et non en état : l'ancre ne s'affiche pas, la re-rendre serait du churn
   *   sur toute la grille.
   */
  const anchorRef = useRef<number | null>(null);

  /**
   * Bascule d'une tuile, avec prise en charge du maj-clic (desktop ; au tactile il n'y a
   * pas de `shiftKey`, le comportement est donc inchangé). Une plage AJOUTE à la
   * sélection, elle ne la remplace pas.
   */
  const handleToggleSelect = useCallback(
    (id: string, index: number, shiftKey: boolean) => {
      const anchor = anchorRef.current;
      if (shiftKey && anchor !== null) {
        const from = Math.min(anchor, index);
        const to = Math.max(anchor, index);
        const ids: string[] = [];
        for (let i = from; i <= to; i += 1) {
          // La grille ne dispose que d'un PRÉFIXE DENSE d'assets chargés : ancre et
          // cible étant toutes deux visibles, l'intervalle y est ; on n'en fait
          // pourtant aucune hypothèse et on saute proprement un index non résolu.
          const asset = getAsset(i);
          if (asset) ids.push(asset.id);
        }
        if (ids.length > 0) onSelectRange(ids);
        return;
      }
      anchorRef.current = index;
      onToggleSelect(id);
    },
    [getAsset, onSelectRange, onToggleSelect],
  );

  // Préfixe dense d'assets chargés (jusqu'au premier trou). La virtualisation par
  // rangées suppose des tailles connues ; on ne dispose donc que le contigu.
  const loadedAssets = useMemo(() => {
    const upTo = Math.min(total, loadedCount);
    const list: GalleryAsset[] = [];
    for (let i = 0; i < upTo; i += 1) {
      const asset = getAsset(i);
      if (!asset) break;
      list.push(asset);
    }
    return list;
    // `getAsset` est stable ; on recompose quand le total OU la frontière chargée
    // (`loadedCount`, bumpée à chaque page reçue) change → la grille se remplit.
  }, [total, loadedCount, getAsset]);

  const layout = useMemo(
    () => computeJustifiedLayout(loadedAssets, width, density.targetRowHeight, GRID_GAP),
    [loadedAssets, width, density.targetRowHeight],
  );

  // Nombre de tuiles disposées → index du premier asset non encore couvert.
  const placedCount = useMemo(
    () => layout.rows.reduce((sum, r) => sum + r.tiles.length, 0),
    [layout.rows],
  );

  // Estimation grossière du nombre de rangées restantes (assets non chargés) pour
  // dimensionner le scroller au `total` : tuiles/rangée ≈ moyenne observée.
  const avgPerRow = layout.rows.length > 0 ? placedCount / layout.rows.length : 1;
  const remaining = Math.max(0, total - placedCount);
  const estimatedExtraRows = Math.ceil(remaining / Math.max(1, avgPerRow));
  const rowCount = layout.rows.length + estimatedExtraRows;

  const rowHeightFor = (index: number): number => {
    const row = layout.rows[index];
    return (row ? row.height : density.targetRowHeight) + GRID_GAP;
  };

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: rowHeightFor,
    overscan: 3,
  });

  const virtualRows = virtualizer.getVirtualItems();

  // Index absolu de la DERNIÈRE rangée visible — scalaire STABLE. `virtualRows` a une
  // nouvelle identité de tableau à CHAQUE rendu : l'utiliser en dépendance d'effet
  // relançait `ensureRange`/`planPages` sur des rendus SANS RAPPORT (frappe de
  // recherche, toggle de sélection, densité…), hors défilement. On dérive l'unique
  // valeur dont l'effet dépend réellement.
  const lastVirtualRow = virtualRows.length > 0 ? virtualRows[virtualRows.length - 1] : undefined;
  const lastVisibleRowIndex = lastVirtualRow ? lastVirtualRow.index : -1;

  // Charge les pages couvrant les rangées visibles (fenêtrage). On calcule un index
  // d'asset absolu de FIN de fenêtre — y compris dans la zone ESTIMÉE (rangées non
  // encore chargées) via une projection `placedCount + rangéesAu-delà × avgPerRow` —
  // puis on demande le chargement contigu jusqu'à `end + tampon`. Le hook plafonne le
  // nombre de pages par tick (anti-rafale 429) ; le reste vient aux ticks suivants.
  useEffect(() => {
    if (lastVisibleRowIndex < 0) return;

    let endAsset: number;
    if (lastVisibleRowIndex < layout.rows.length) {
      // Rangée réellement disposée : index absolu exact de sa dernière tuile.
      const lastRow = layout.rows[lastVisibleRowIndex];
      endAsset = lastRow ? lastRow.startIndex + lastRow.tiles.length : placedCount;
    } else {
      // Rangée estimée (au-delà du préfixe chargé) : on projette combien d'assets ce
      // niveau de défilement représente pour cibler les bonnes pages à charger.
      const extraRows = lastVisibleRowIndex - layout.rows.length + 1;
      endAsset = placedCount + Math.ceil(extraRows * Math.max(1, avgPerRow));
    }

    // Tampon d'anticipation : ~2 rangées de plus (préchargement fluide).
    const buffer = Math.ceil(2 * Math.max(1, avgPerRow));
    ensureRange(placedCount, Math.min(total, endAsset + buffer));
  }, [lastVisibleRowIndex, layout.rows, placedCount, avgPerRow, total, ensureRange]);

  return (
    <div
      ref={scrollRef}
      className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
      role="grid"
      aria-rowcount={rowCount}
    >
      <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {virtualRows.map((vRow) => {
          const row: LayoutRow<GalleryAsset> | undefined = layout.rows[vRow.index];
          // Index absolu du premier asset de la rangée (pour la visionneuse), porté par
          // la disposition elle-même (audit B3) — plus de recherche linéaire par rendu.
          const rowStart = row ? row.startIndex : 0;
          return (
            <div
              key={vRow.key}
              role="row"
              className="absolute inset-x-0 flex"
              style={{
                top: 0,
                transform: `translateY(${vRow.start}px)`,
                height: row ? row.height : density.targetRowHeight,
                gap: GRID_GAP,
              }}
            >
              {row ? (
                row.tiles.map((tile, tileIndex) => (
                  <div role="gridcell" key={tile.asset.id}>
                    <Thumbnail
                      asset={tile.asset}
                      index={rowStart + tileIndex}
                      width={tile.width}
                      height={tile.height}
                      size={density.thumbnailSize}
                      selected={selected.has(tile.asset.id)}
                      selectionMode={selectionMode}
                      onToggleSelect={handleToggleSelect}
                      onOpen={onOpenAsset}
                    />
                  </div>
                ))
              ) : (
                // Rangée non encore chargée : skeleton pleine largeur (anti-CLS).
                <div
                  className="w-full animate-pulse rounded-md bg-surface-2 motion-reduce:animate-none"
                  aria-hidden="true"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
